import clsx from 'clsx';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import toolbarStyles from '../PlateToolbar/PlateToolbar.module.scss';
import styles from './AIToolbarButton.module.scss';
import { useFeatureVisibility } from '../../../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../../../utils/constants/visibilityKeys';

// The AI menu lives inside PlateEditor (it owns the imperative ref). Rather than
// drill that ref to every toolbar, this button dispatches a `plate-ai-toggle`
// event on `document`; PlateEditor listens and calls `aiMenuRef.current.toggle()`.
// Works regardless of where the toolbar renders (PlateToolbar portals to its
// host slot, FloatingToolbar portals to <body>).
export const AI_TOGGLE_EVENT = 'plate-ai-toggle';

interface AIToolbarButtonProps {
    /**
     * Override the default PlateToolbar button class. FloatingToolbar passes its
     * own `styles.btn` so the button matches its host toolbar's chrome.
     */
    className?: string;
    /** Visible label + tooltip text (the ⌘J hint is appended to the title). */
    label?: string;
    /**
     * Render the icon only (no text). The compact FloatingToolbar uses this so
     * the "Ask Aica" label doesn't widen the button past the bubble's chrome
     * and overflow it. The label still drives the tooltip + aria-label.
     */
    iconOnly?: boolean;
}

export function AIToolbarButton({ className, label = 'Ask Aica', iconOnly = false }: AIToolbarButtonProps) {
    // Gate render on `editor.AI.enabled`.write (level 1). Missing key, read (0),
    // or hidden (-1) all return false. Mirrors the PlateEditor-side listener gate
    // so the surface is consistently absent when AI is off.
    const { aiEnabled } = useFeatureVisibility({ aiEnabled: VISIBILITY_KEYS.AI.ENABLED });
    if (!aiEnabled.write) return null;

    const handleMouseDown = (e: React.MouseEvent) => {
        // preventDefault keeps the editor's selection alive so the AI menu can
        // read the selected text as prompt context.
        e.preventDefault();
        document.dispatchEvent(new CustomEvent(AI_TOGGLE_EVENT));
    };

    return (
        <button
            type="button"
            data-ai-trigger
            className={clsx(className ?? toolbarStyles.btn, !iconOnly && styles.aiBtn)}
            onMouseDown={handleMouseDown}
            title={`${label} (⌘J / Ctrl+J)`}
            aria-label={label}
            aria-keyshortcuts="Meta+J Control+J"
        >
            <AutoAwesomeIcon fontSize="small" />
            {!iconOnly && <span className={styles.label}>{label}</span>}
        </button>
    );
}
